import { useMemo } from 'react'
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Flex,
  Input,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Typography,
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { TeamOutlined } from '@ant-design/icons'
import { useQuery } from '@tanstack/react-query'
import dayjs, { type Dayjs } from 'dayjs'
import { useTranslation } from 'react-i18next'
import { translateError } from '@/shared/api/errors'
import { queryKeys } from '@/shared/api/queryKeys'
import { fetchTurnover, type TurnoverRow } from '@/features/hr/turnoverApi'
import { tenantToday } from '@/shared/lib/dayRange'
import { formatNumber } from '@/shared/lib/format'
import {
  applyLocalList,
  byNumber,
  byText,
  localSortChange,
  sortColumn,
  useLocalListState,
} from '@/shared/lib/localList'
import { EmptyState } from '@/shared/ui/EmptyState'
import { PageHeader } from '@/shared/ui/PageHeader'
import { PermissionNotice } from '@/shared/ui/PermissionNotice'
import { usePermissions } from '@/shared/auth/usePermissions'

const ISO_DAY = 'YYYY-MM-DD'

/**
 * Kadrlar oqimi — mijoz hujjatining «Employee turnover» bandi
 * (`GET /api/hr/reports/turnover`).
 *
 * ⚠️ OY TANLANADI, ORALIQ EMAS. Kadrlar oqimi oy (yoki chorak) bilan
 * o'lchanadigan ko'rsatkich va ixtiyoriy sana oralig'i uni ma'nosiz
 * qilardi: "12-martdan 3-aprelgacha oqim" degan son bilan hech narsa
 * taqqoslab bo'lmaydi. Backend baribir ixtiyoriy oraliqni qabul qiladi
 * — bu yerdagi cheklov EKRANNIKI, kontraktniki emas.
 *
 * ⚠️ `to` sifatida KEYINGI oyning birinchi kuni yuboriladi (oraliq
 * yarim ochiq). "Oyning oxirgi kuni" ni hisoblash 30/31 kunli oylar va
 * kabisa fevralida bir kunni jimgina tashlab ketardi.
 *
 * ⚠️ FOIZ `null` BO'LSA "—", "0%" EMAS. Bo'sh filialda savolning o'zi
 * qo'yilmagan; nol ko'rsatish "oqim yo'q, hammasi joyida" degan
 * YOLG'ON javob bo'lardi (`IncomeStatement#margin` bilan bir xil
 * qoida, `FinanceBranchReportPage` da ham shunday).
 *
 * ⚠️ `RequirePermission` marshrutda BOR (`hr.person.read`), lekin bu
 * yerda `PermissionNotice` ham chizadi: sahifaga to'g'ridan-to'g'ri
 * havola bilan kelgan odam «Ruxsat yo'q» o'rniga AYNAN QAYSI huquq
 * kerakligini ko'rsin (`HrSalariesPage` bilan bir xil mulohaza).
 *
 * ⛔ SAHIFALASH YO'Q — SERVER UNI QO'LLAB-QUVVATLAMAYDI.
 * `TurnoverController#turnover` faqat `from` va `to` ni oladi:
 * `page`/`size`/`sort`/`q` YO'Q. Javob bo'limlar ro'yxatini TO'LIQ
 * beradi (kesilgan bo'lsa `truncated` bilan aytadi), shuning uchun
 * soxta sahifalash yasalmadi (egasi qarori, 2026-08-20).
 *
 * ⚠️ SUKUT BO'YICHA `created desc` YO'Q — VA BU ONGLI QAROR.
 * Qatorlar — BO'LIMLAR, ular "qo'shilish" vaqti bo'yicha o'qilmaydi va
 * hisobotda bunday maydon umuman qaytmaydi (`TurnoverReport.Row`).
 * Vaqtni o'ylab topib saralash yolg'on tartib bo'lardi. Standart
 * tartib SERVERNIKI qoladi, operator esa ustun sarlavhasidan
 * "ko'p ketganlar" yoki "yuqori foiz" tartibini tanlaydi — hisobotda
 * eng ko'p so'raladigan savol aynan shu.
 *
 * ⚠️ YAKUN JADVALGA BOG'LIQ EMAS. Yuqoridagi to'rt kartochka SERVER
 * bergan `total` dan chiziladi va u qatorlar yig'indisi EMAS
 * (`turnoverApi` 3-qoidasi). Ya'ni qidiruv yoki filtr yakunni
 * o'zgartirmaydi — bu ekranda OCHIQ yoziladi, aks holda "filtrladim,
 * yakun o'zgarmadi" degan savol tug'ilardi. Jadvalning O'ZIDA esa
 * yig'indi QATORI YO'Q va bo'lmaydi ham: ro'yxat ustida hisoblangan
 * jami serverning yakuni bilan zid bo'lardi.
 */
export function HrTurnoverPage() {
  const { t } = useTranslation()
  const { can } = usePermissions()
  const canRead = can('hr.person.read')

  /**
   * ⚠️ OY VA FILTR MANZILDA. Hisobotda eng ko'p ulashiladigan narsa —
   * AYNAN qaysi oy ko'rilgani; `useState` da u havolaga tushmasdi va
   * sahifa yangilanganda joriy oyga qaytardi.
   *
   * ⚠️ `month` — KONTEKST, filtr emas: u savolning o'zini belgilaydi
   * va «Tozalash» uni tegmasligi kerak (`narrowingKeys`).
   */
  const list = useLocalListState({ narrowingKeys: ['moved'] })

  // ⚠️ `dayjs()` EMAS, `tenantToday()`: argumentsiz `dayjs()`
  //    BRAUZER zonasidagi "hozir" ni oladi va `Asia/Kabul` (UTC+4:30)
  //    da kuniga yarim soat davomida brauzer allaqachon keyingi oyda,
  //    tenant esa hali oldingisida — o'sha oynada ekran BOSHQA oyni
  //    ochib turardi, xatosiz (`dayRange.ts` javadoc'i). Buni
  //    `tests/timezone.mjs` §16 qorovuli ushladi.
  //
  // ⚠️ Yaroqsiz qiymat (qo'lda tahrirlangan havola) sahifani
  //    YIQITMAYDI — joriy oyga qaytadi.
  const monthParam = list.filter('month')
  const month: Dayjs = useMemo(() => {
    if (monthParam === null) return tenantToday().startOf('month')
    const parsed = dayjs(monthParam)
    return parsed.isValid() ? parsed.startOf('month') : tenantToday().startOf('month')
  }, [monthParam])

  const from = month.startOf('month').format(ISO_DAY)
  // ⚠️ KEYINGI oyning birinchi kuni — oraliq oxiri KIRITILMAYDI.
  const to = month.startOf('month').add(1, 'month').format(ISO_DAY)

  const report = useQuery({
    queryKey: queryKeys.hr.turnover(from, to),
    queryFn: ({ signal }) => fetchTurnover({ from, to }, signal),
    // ⚠️ Huquq yo'q bo'lsa SO'ROV UMUMAN YUBORILMAYDI — panel 403 ni
    //    ko'rib emas, huquqni bilib to'xtaydi (`docs/04-ROLES.md` §8).
    enabled: canRead,
  })

  const total = report.data?.total

  const serverRows = useMemo(() => report.data?.rows ?? [], [report.data])

  /*
    ⚠️ QIDIRUV VA FILTR MIJOZDA: `TurnoverController` ularni umuman
    olmaydi (faqat `from`/`to`). Va bu yolg'on tartib bermaydi —
    javobda bo'limlar TO'LIQ keladi, ya'ni saralanadigan massiv
    hisobotning o'zi.

    ⚠️ `turnoverPercent` bo'yicha saralashda `null` — "hisoblab
    bo'lmadi" (o'rtacha shtat nol), eng past foiz EMAS. Uni nolga
    aylantirish bo'sh filialni "eng barqaror jamoa" qilib ko'rsatardi.
  */
  const rows = useMemo(
    () =>
      applyLocalList(serverRows, {
        search: list.search,
        searchFields: (row) => [row.nodeName, row.nodeCode],
        filters: {
          moved: (row) => row.hired > 0 || row.terminated > 0,
        },
        filterState: list.state.filters,
        comparators: {
          node: byText((row) => row.nodeName),
          start: byNumber((row) => row.headcountStart),
          hired: byNumber((row) => row.hired),
          terminated: byNumber((row) => row.terminated),
          end: byNumber((row) => row.headcountEnd),
          rate: (a, b) => {
            if (a.turnoverPercent === null && b.turnoverPercent === null) return 0
            if (a.turnoverPercent === null) return -1
            if (b.turnoverPercent === null) return 1
            return a.turnoverPercent - b.turnoverPercent
          },
        },
        sort: list.sort,
      }),
    [serverRows, list.search, list.state.filters, list.sort],
  )

  const columns: ColumnsType<TurnoverRow> = [
    {
      key: 'node',
      title: t('hr.turnover.columns.node'),
      ...sortColumn(list.sort, 'node'),
      render: (_, row) =>
        row.nodeId === null ? (
          <Typography.Text type="secondary" italic>
            {t('hr.turnover.unassigned')}
          </Typography.Text>
        ) : (
          <Typography.Text strong>{row.nodeName}</Typography.Text>
        ),
    },
    {
      key: 'start',
      align: 'right',
      title: t('hr.turnover.columns.start'),
      ...sortColumn(list.sort, 'start'),
      render: (_, row) => formatNumber(row.headcountStart),
    },
    {
      key: 'hired',
      align: 'right',
      title: t('hr.turnover.columns.hired'),
      ...sortColumn(list.sort, 'hired'),
      render: (_, row) => formatNumber(row.hired),
    },
    {
      key: 'terminated',
      align: 'right',
      title: t('hr.turnover.columns.terminated'),
      ...sortColumn(list.sort, 'terminated'),
      render: (_, row) => formatNumber(row.terminated),
    },
    {
      key: 'end',
      align: 'right',
      title: t('hr.turnover.columns.end'),
      ...sortColumn(list.sort, 'end'),
      render: (_, row) => formatNumber(row.headcountEnd),
    },
    {
      key: 'rate',
      align: 'right',
      title: t('hr.turnover.columns.rate'),
      ...sortColumn(list.sort, 'rate'),
      render: (_, row) => <PercentText value={row.turnoverPercent} />,
    },
  ]

  if (!canRead) {
    return (
      <>
        <PageHeader title={t('hr.turnover.title')} description={t('hr.turnover.intro')} />
        <Card>
          <PermissionNotice
            permission="hr.person.read"
            title={t('hr.turnover.title')}
            reason={t('hr.turnover.hiddenReason')}
          />
        </Card>
      </>
    )
  }

  return (
    <>
      <PageHeader title={t('hr.turnover.title')} description={t('hr.turnover.intro')} />

      <Card size="small" style={{ marginBottom: 16 }}>
        <Space wrap size="middle" align="center">
          <Typography.Text type="secondary">{t('hr.turnover.monthLabel')}:</Typography.Text>
          <DatePicker
            picker="month"
            value={month}
            onChange={(value) => {
              if (value) list.setFilter('month', value.startOf('month').format('YYYY-MM'))
            }}
            allowClear={false}
            format="MMMM YYYY"
          />
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {t('hr.turnover.formula')}
          </Typography.Text>
        </Space>
      </Card>

      {report.isError ? (
        <Alert
          type="error"
          showIcon
          message={t('dataTable.loadFailed')}
          description={translateError(report.error, t)}
        />
      ) : (
        <>
          <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
            <Col xs={12} md={6}>
              <Card size="small" loading={report.isPending}>
                <Statistic
                  title={t('hr.turnover.kpi.start')}
                  value={formatNumber(total?.headcountStart ?? 0)}
                />
              </Card>
            </Col>
            <Col xs={12} md={6}>
              <Card size="small" loading={report.isPending}>
                <Statistic
                  title={t('hr.turnover.kpi.hired')}
                  value={formatNumber(total?.hired ?? 0)}
                />
              </Card>
            </Col>
            <Col xs={12} md={6}>
              <Card size="small" loading={report.isPending}>
                <Statistic
                  title={t('hr.turnover.kpi.terminated')}
                  value={formatNumber(total?.terminated ?? 0)}
                />
              </Card>
            </Col>
            <Col xs={12} md={6}>
              <Card size="small" loading={report.isPending}>
                {/*
                  ⚠️ Yakuniy foiz jadval qatorlaridan HISOBLANMAYDI — u
                  javobning `total` maydonidan keladi. Foizlarni
                  o'rtachalash kichik filiallarni katta filial bilan
                  teng vaznda ko'rsatardi va son butunlay boshqa
                  bo'lardi (`TurnoverReport` javadoc'idagi misol).
                */}
                <Statistic
                  title={t('hr.turnover.kpi.rate')}
                  valueRender={() => <PercentText value={total?.turnoverPercent ?? null} />}
                  value={0}
                />
              </Card>
            </Col>
          </Row>

          {/*
            ⚠️ Kesilish JIM QOLMAYDI: `total` to'liq, jadval esa emas.
            Buni aytmasak "filiallar yig'indisi yakunga teng emas" degan
            javobsiz savol qolardi.
          */}
          {report.data?.truncated ? (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
              message={t('hr.turnover.truncated')}
              description={t('hr.turnover.truncatedHint')}
            />
          ) : null}

          <Card title={t('hr.turnover.tableTitle')} size="small">
            {/* Qidiruv va filtr KO'RINADIGAN joyda — ustun voronkasi
                ichidagi boshqaruvni operator topmaydi. */}
            <Flex wrap gap="small" align="center" style={{ marginBottom: 12 }}>
              <Input.Search
                allowClear
                value={list.searchText}
                onChange={(e) => list.setSearchText(e.target.value)}
                placeholder={t('hr.turnover.searchPlaceholder')}
                style={{ width: 260, maxWidth: '100%' }}
              />
              {/*
                ⚠️ YAGONA MA'NOLI FILTR: hisobotda bo'limlarning ko'pi
                "0 kelgan, 0 ketgan" bo'lib turadi va ular javobga
                aloqasi bo'lmagan shovqin. Boshqa filtr o'ylab
                topilmadi — bo'lim turi ham, holati ham bu javobda
                umuman qaytmaydi.
              */}
              <Select<string>
                allowClear
                value={list.filter('moved')}
                onChange={(value) => list.setFilter('moved', value ?? null)}
                placeholder={t('hr.turnover.movedOnly')}
                style={{ width: 240 }}
                options={[{ value: 'yes', label: t('hr.turnover.movedOnly') }]}
              />
              {list.narrowed ? (
                <>
                  <Typography.Text type="secondary">
                    {t('listView.shown', { shown: rows.length, total: serverRows.length })}
                  </Typography.Text>
                  <Button type="link" size="small" onClick={list.reset}>
                    {t('dataTable.clearFilters')}
                  </Button>
                </>
              ) : null}
            </Flex>

            <Table<TurnoverRow>
              rowKey={(row) => row.nodeId ?? '__unassigned__'}
              columns={columns}
              dataSource={[...rows]}
              loading={report.isPending}
              pagination={false}
              size="small"
              onChange={localSortChange<TurnoverRow>(list.patch)}
              locale={{
                emptyText:
                  // ⚠️ «Hisobotda bo'lim yo'q» va «qidiruv topmadi» —
                  //    ikki xil holat va ikki xil harakat: birinchisida
                  //    boshqa oy, ikkinchisida filtrni tozalash.
                  serverRows.length > 0 ? (
                    <EmptyState
                      variant="no-data"
                      title={t('listView.noMatch')}
                      description={t('listView.noMatchHint', { total: serverRows.length })}
                      actionLabel={t('dataTable.clearFilters')}
                      onAction={list.reset}
                    />
                  ) : (
                    <EmptyState
                      icon={<TeamOutlined />}
                      title={t('hr.turnover.empty')}
                      description={t('hr.turnover.emptyHint')}
                    />
                  ),
              }}
            />

            <Typography.Paragraph
              type="secondary"
              style={{ fontSize: 12, marginTop: 12, marginBottom: 0 }}
            >
              {`${t('hr.turnover.totalNote')} ${t('hr.turnover.orderNote')}`}
            </Typography.Paragraph>
          </Card>
        </>
      )}
    </>
  )
}

/**
 * Foiz yoki "—".
 *
 * ⚠️ `null` — "hisoblab bo'lmadi" (o'rtacha shtat nol), "nol foiz"
 * EMAS. Ikkalasini bir xil ko'rsatish rahbarga bo'sh filialni
 * "barqaror jamoa" qilib ko'rsatardi.
 */
function PercentText({ value }: { value: number | null }) {
  if (value === null) {
    return <Typography.Text type="secondary">—</Typography.Text>
  }
  return <Typography.Text>{value.toFixed(2)}%</Typography.Text>
}
